import { useState, useCallback } from 'react';
import { Clock } from 'lucide-react';
import { ItemInput } from './components/ItemInput';
import { ScoringView } from './components/ScoringView';
import { ResultsView } from './components/ResultsView';
import type { CostOfDelayItem } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { LiveSequentialScore } from '../../components/prioritization/session/liveExperience/liveTools/LiveSequentialScore';
import { LiveToolComplete } from '../../components/prioritization/session/liveExperience/liveTools/LiveToolComplete';

type AppState = 'input' | 'scoring' | 'results';

const COD_DEFINITIONS: Record<'valueLost' | 'costToRestart', string> = {
  valueLost: 'The economic value lost per unit of time if this item is delayed or deprioritized. Higher means delay is more costly.',
  costToRestart: 'The overhead cost to resume this item after a pause — including ramp-up time, context loss, and re-coordination.',
};

function CostOfDelayContent(props: ToolHostProps) {
  const { isLiveSession, shouldAutoStart } = useLiveToolSession();

  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<CostOfDelayItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scoringPhase, setScoringPhase] = useState<'valueLost' | 'costToRestart'>('valueLost');
  // Live: the participant pressed Finish on the last item and sees their ranking.
  const [liveDone, setLiveDone] = useState(false);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: CostOfDelayItem[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      valueLost: null,
      costToRestart: null,
      costOfDelay: null,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    setItems(prev => [...prev, ...newItems]);

    if (shouldAutoStart) {
      setCurrentItemIndex(0);
      setScoringPhase('valueLost');
      setState('scoring');
    }
  }, [shouldAutoStart]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by Cost of Delay (highest first - highest urgency)
    const sortedItems = [...items]
      .filter(item => item.costOfDelay !== null)
      .sort((a, b) => (b.costOfDelay || 0) - (a.costOfDelay || 0));
    
    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: idx + 1,
      score: item.costOfDelay || 0,
      scoreBreakdown: {
        valueLost: item.valueLost || 0,
        costToRestart: item.costToRestart || 0,
        costOfDelay: item.costOfDelay || 0,
      },
      metadata: {
        valueLost: item.valueLost,
        costToRestart: item.costToRestart,
        costOfDelay: item.costOfDelay,
      }
    }));
  }, [items, sourceIdMap]);

  const handleStart = (itemNames: string[]) => {
    const newItems: CostOfDelayItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      valueLost: null,
      costToRestart: null,
      costOfDelay: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setScoringPhase('valueLost');
    setState('scoring');
  };
  
  const handleScoreUpdate = (field: 'valueLost' | 'costToRestart', value: number | null) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      [field]: value,
      // Worked out again at Finish, so a changed score is never submitted stale.
      costOfDelay: null,
    };
    setItems(updatedItems);
  };

  const handleNext = () => {
    if (scoringPhase === 'valueLost') {
      setScoringPhase('costToRestart');
    } else if (scoringPhase === 'costToRestart') {
      // Move to next item or finish
      if (currentItemIndex < items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
        setScoringPhase('valueLost');
      } else {
        // Calculate Cost of Delay for all items
        const calculatedItems = items.map((item) => {
          if (item.valueLost !== null && item.costToRestart !== null) {
            const costOfDelay = item.valueLost + item.costToRestart;
            return { ...item, costOfDelay };
          }
          return item;
        });
        setItems(calculatedItems);
        if (isLiveSession) setLiveDone(true);
        else setState('results');
      }
    }
  };

  const handleBack = () => {
    if (scoringPhase === 'costToRestart') {
      setScoringPhase('valueLost');
    } else if (scoringPhase === 'valueLost' && currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setScoringPhase('costToRestart');
    }
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setScoringPhase('valueLost');
    setSourceIdMap({});
    setLiveDone(false);
  };

  const canProceed = () => {
    const current = items[currentItemIndex];
    if (!current) return false;
    if (scoringPhase === 'valueLost') {
      return current.valueLost !== null && current.valueLost >= 0;
    }
    if (scoringPhase === 'costToRestart') {
      return current.costToRestart !== null && current.costToRestart >= 0;
    }
    return false;
  };

  const currentItem = items[currentItemIndex];
  const canSubmitLive = isLiveSession && items.length > 0 && items.every(i => i.costOfDelay != null);
  const phaseValue =
    currentItem &&
    (scoringPhase === 'valueLost' ? currentItem.valueLost : currentItem.costToRestart);

  return (
    <ToolWrapper
      toolName="Cost of Delay"
      toolDescription="Assess the cost of deferring attention or action"
      onImportItems={handleImportItems}
      itemCount={items.length}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      {...props}
    >
      <div className="w-full">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-[var(--rule)]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Clock className="h-8 w-8 text-[var(--text-ter)] flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-[var(--text-pri)]">
                  Cost of Delay
                </h1>
                <p className="text-sm text-[var(--text-sec)] mt-1">
                  Assess the cost of deferring attention, a decision, or action on an item. CoD = Value Lost + Cost to Restart.
                </p>
              </div>
            </div>
            {state !== 'input' && (
              <button
                onClick={handleReset}
                className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] whitespace-nowrap"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              <ItemInput onStart={handleStart} />
            </>
          )}

          {state === 'scoring' && isLiveSession && liveDone && (
            <LiveToolScaffold
              title="Cost of delay"
              subtitle="Your ranking is ready to submit"
              progressDone={items.length * 2}
              progressTotal={items.length * 2}
            >
              <LiveToolComplete
                heading="All items scored"
                items={handleGetResults().map((r, i) => ({
                  id: r.id,
                  name: r.name,
                  rank: r.rank ?? i + 1,
                  detail: `CoD ${(r.score ?? 0).toLocaleString()}`,
                }))}
                onChangeScores={() => setLiveDone(false)}
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && isLiveSession && !liveDone && (
            <LiveToolScaffold
              title="Cost of delay"
              subtitle="Value lost + cost to restart for each item"
              progressDone={currentItemIndex * 2 + (scoringPhase === 'costToRestart' ? 1 : 0)}
              progressTotal={items.length * 2}
            >
              <LiveSequentialScore
                itemName={currentItem.name}
                itemIndex={currentItemIndex}
                totalItems={items.length}
                phaseTitle={scoringPhase === 'valueLost' ? 'Value lost' : 'Cost to restart'}
                valueDefinition={COD_DEFINITIONS[scoringPhase]}
                phaseDescription={
                  scoringPhase === 'valueLost'
                    ? 'Value lost per week/month if delayed'
                    : 'Cost to restart if paused then resumed'
                }
                placeholder="e.g. 10000"
                inputValue={phaseValue?.toString() ?? ''}
                onInputChange={v => {
                  const num = v.trim() === '' ? null : parseFloat(v);
                  handleScoreUpdate(scoringPhase, num != null && !isNaN(num) ? num : null);
                }}
                onNext={handleNext}
                onBack={canProceed() ? handleBack : undefined}
                canProceed={canProceed()}
                isLastStep={
                  scoringPhase === 'costToRestart' && currentItemIndex === items.length - 1
                }
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && currentItem && !isLiveSession && (
            <ScoringView
              item={currentItem}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              phase={scoringPhase}
              onScoreUpdate={handleScoreUpdate}
              onNext={handleNext}
              onBack={handleBack}
              canProceed={canProceed()}
              progress={((currentItemIndex * 2 + (scoringPhase === 'valueLost' ? 0 : 1)) / (items.length * 2)) * 100}
            />
          )}

          {state === 'results' && (
            <ResultsView 
              items={items} 
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function CostOfDelay(props: ToolHostProps) {
  return <CostOfDelayContent {...props} />;
}
